/**
 * „Rückgängig" für das Notizfeld (T-282, Admin 12.10.2026: „Zurück Button").
 *
 * Die eingebaute Rückgängig-Funktion des Browsers (Strg+Z) kennt nur, was er
 * selbst getan hat — Tippen, `execCommand`. Die Palette schreibt danach aber
 * selbst ins DOM (Marker → Klassen, Formatierung entfernen, gereinigtes
 * Einfügen); diese Schritte sieht der Browser nicht und kann sie nicht
 * zurücknehmen. Deshalb führt das Feld für GENAU diese Schritte einen
 * eigenen kleinen Verlauf: Zustand davor und danach.
 *
 * Zurückgenommen wird nur, wenn das Feld noch GENAU so aussieht wie direkt
 * nach dem Schritt. Hat jemand inzwischen getippt oder hat das Speichern
 * Zuordnungen geändert, stimmt der Zustand nicht mehr — dann bleibt alles,
 * wie es ist, und Strg+Z fällt an den Browser zurück. Ein Schnappschuss von
 * vorhin über neuere Arbeit zu legen, verlöre Text.
 */

import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
type Schritt = { vorher: string; nachher: string };

const MAX_SCHRITTE = 50;
const verlaeufe = new WeakMap<HTMLElement, Schritt[]>();

/** `fn` ausführen und — wenn sie das Feld verändert hat — als Schritt merken. */
export function mitVerlauf(feld: HTMLElement | null, fn: () => void): void {
    if (!feld) {
        fn();
        return;
    }
    const vorher = feld.innerHTML;
    fn();
    const nachher = feld.innerHTML;
    if (vorher === nachher) return;
    const stapel = verlaeufe.get(feld) ?? [];
    stapel.push({ vorher, nachher });
    if (stapel.length > MAX_SCHRITTE) stapel.shift();
    verlaeufe.set(feld, stapel);
}

/**
 * Einen Schritt merken, der sich über mehrere Ereignisse zieht — das Ziehen
 * am Griff eines Bilds (User 23.10.2026): `vorher` beim Greifen, der
 * Zustand danach beim Loslassen. Ändert sich nichts, bleibt der Verlauf, wie
 * er ist.
 */
export function merkeSchritt(feld: HTMLElement | null, vorher: string): void {
    if (!feld) return;
    const nachher = feld.innerHTML;
    if (vorher === nachher) return;
    const stapel = verlaeufe.get(feld) ?? [];
    stapel.push({ vorher, nachher });
    if (stapel.length > MAX_SCHRITTE) stapel.shift();
    verlaeufe.set(feld, stapel);
}

/** Lässt sich der letzte Schritt jetzt zurücknehmen? */
export function kannRueckgaengig(feld: HTMLElement | null): boolean {
    if (!feld) return false;
    const stapel = verlaeufe.get(feld);
    const letzter = stapel?.[stapel.length - 1];
    return !!letzter && letzter.nachher === feld.innerHTML;
}

/**
 * Den letzten Schritt zurücknehmen. Liefert `false`, wenn es keinen passenden
 * gibt — dann verwirft es den veralteten Verlauf, damit kein älterer Schritt
 * später doch noch über neue Arbeit gelegt wird.
 */
export function rueckgaengig(feld: HTMLElement | null): boolean {
    if (!feld) return false;
    const stapel = verlaeufe.get(feld);
    if (!stapel?.length) return false;
    if (!kannRueckgaengig(feld)) {
        verlaeufe.delete(feld);
        return false;
    }
    const letzter = stapel.pop()!;
    feld.innerHTML = letzter.vorher;
    // Cursor ans Ende des Felds — die alte Auswahl zeigt in ersetzte Knoten.
    feld.focus();
    const sel = aktiveAuswahl();
    if (sel) {
        const r = aktivesDokument().createRange();
        r.selectNodeContents(feld);
        r.collapse(false);
        sel.removeAllRanges();
        sel.addRange(r);
    }
    return true;
}
